/**
 * AI Insights tab for Campaign Report page. (FEAT-0012.)
 *
 * Replaces the prior "AI Insights" modal-from-button UX. Now:
 *  - Tab loads the LATEST persisted analysis for this campaign from
 *    RRS (`GET /campaigns/:id/ai-insights/latest`) on mount.
 *  - States: loading · no-insights (offer Run) · saved (render
 *    narrative) · stale (orange badge + Re-run prompt) · running.
 *  - On Run: kicks off the agent via the agents-console gateway;
 *    gateway write-through persists the result; tab refreshes.
 *
 * Self-contained — no modal. The agents-console gateway handles the
 * long-running agent call; this tab just shows status while waiting.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  VStack,
  Text,
  Spinner,
  Tag,
  TagLabel,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  Code,
  Grid,
  Icon,
  useToast
} from "@chakra-ui/react";
import {
  LuSparkles,
  LuRefreshCw,
  LuTrendingUp,
  LuTrendingDown,
  LuTarget,
  LuAlertTriangle,
  LuClock,
  LuMinus,
  LuRepeat
} from "react-icons/lu";
// LuChartBar isn't exported in this version of react-icons; the
// statistical-confidence section uses LuTarget as a stand-in.
import { runAgent, getLatestCampaignInsights } from "../../../components/AIAgents/aiAgentsClient";

export default function AiInsightsTab({ campaignId }) {
  const [phase,   setPhase]   = useState("loading"); // loading | empty | saved | running | error
  const [data,    setData]    = useState(null);       // wrapped row from RRS (snapshot/live/stale)
  const [error,   setError]   = useState(null);
  const [runLog,  setRunLog]  = useState("");
  const toast = useToast();

  const loadLatest = useCallback(async () => {
    setPhase("loading");
    setError(null);
    try {
      const r = await getLatestCampaignInsights(campaignId);
      if (r?.has_insights && r?.data) {
        setData(r.data);
        setPhase("saved");
      } else {
        setData(null);
        setPhase("empty");
      }
    } catch (e) {
      setError(e.message || "Failed to load AI insights");
      setPhase("error");
    }
  }, [campaignId]);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  const runAnalysis = useCallback(async () => {
    setPhase("running");
    setError(null);
    setRunLog("");
    try {
      const out = await runAgent("campaign-performance-analyst", {
        campaign_id: Number(campaignId),
        baseline_days: 90,
      });
      setRunLog(out?.stdout?.slice(-2000) || "");
      if (!out?.ok) {
        setError(out?.error || "Agent run failed");
        setPhase("error");
        return;
      }
      // Gateway already wrote through to RRS — re-fetch latest.
      await loadLatest();
      toast({ title: "AI Insights generated", status: "success", duration: 2000 });
    } catch (e) {
      setError(e.message || "Run failed");
      setPhase("error");
    }
  }, [campaignId, loadLatest, toast]);

  // ─── Render states ─────────────────────────────────────────

  if (phase === "loading") {
    return (
      <Flex direction="column" align="center" py={10} gap={2}>
        <Spinner size="md" color="purple.500" />
        <Text fontSize="sm" color="gray.500">Loading saved AI Insights…</Text>
      </Flex>
    );
  }

  if (phase === "error") {
    return (
      <Box>
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          <Box flex="1">
            <Text fontWeight="600" fontSize="sm">Couldn't load AI Insights</Text>
            <Text fontSize="xs" color="gray.700" mt={1}>{error}</Text>
            {runLog && (
              <Code mt={2} display="block" p={2} fontSize="xs" whiteSpace="pre-wrap" maxH="160px" overflow="auto">
                {runLog}
              </Code>
            )}
          </Box>
          <Button size="sm" colorScheme="red" variant="outline" onClick={loadLatest}>Retry</Button>
        </Alert>
        <Button size="sm" colorScheme="purple" leftIcon={<LuSparkles />} onClick={runAnalysis}>Run analysis anyway</Button>
      </Box>
    );
  }

  if (phase === "running") {
    return (
      <Flex direction="column" align="center" py={12} gap={3}>
        <Spinner size="lg" color="purple.500" thickness="3px" />
        <Text fontWeight="500" color="gray.700">Generating AI Insights…</Text>
        <Text fontSize="xs" color="gray.500">Fetching campaign data → comparing to baseline → narrating</Text>
      </Flex>
    );
  }

  if (phase === "empty") {
    return <EmptyState onRun={runAnalysis} />;
  }

  // phase === "saved"
  return <SavedInsights data={data} onRerun={runAnalysis} />;
}

// ─── Empty state ───────────────────────────────────────────────────

function EmptyState({ onRun }) {
  return (
    <Flex direction="column" align="center" py={12} gap={4} textAlign="center">
      <Box w={14} h={14} borderRadius="xl" bg="purple.50" display="flex" alignItems="center" justifyContent="center">
        <LuSparkles color="#8B5CF6" size={28} />
      </Box>
      <Box maxW="520px">
        <Text fontSize="lg" fontWeight="600" color="gray.800">No AI analysis yet</Text>
        <Text fontSize="sm" color="gray.600" mt={2}>
          The Campaign Performance Analyst will compare this campaign to your 90-day median,
          identify your funnel's biggest drop-off stage, attribute results to specific channels,
          and recommend three quantified next actions.
        </Text>
      </Box>
      <Button colorScheme="purple" size="md" leftIcon={<LuSparkles />} onClick={onRun}>
        Run AI Insights
      </Button>
    </Flex>
  );
}

// ─── Saved insights renderer ───────────────────────────────────────

function SavedInsights({ data, onRerun }) {
  const payload   = data?.payload || {};
  const narrative = payload.narrative || {};
  const summary   = payload.summary   || {};
  // FEAT-0014 — deterministic decision memo lives at TOP-LEVEL on the
  // payload (always present, even on metrics-only runs). The narrative
  // also carries decision_summary after the merge step, but reading
  // from payload.* is the canonical path.
  const decision    = payload.decision_summary    || narrative.decision_summary    || null;
  const experiments = payload.recommended_experiments || narrative.recommended_experiments || [];
  const dataGaps    = decision?.data_gaps || [];
  const topEvidence = decision?.top_evidence || [];
  const topRisks    = decision?.top_risks    || [];
  const sampleSizeGrade = decision?.sample_size_grade || null;
  const primaryDriver   = decision?.primary_driver   || null;
  const losingChannel   = decision?.losing_channel   || null;
  const whyThisMatters  = decision?.why_this_matters || null;
  const generatedAgo = humanAgo(data.generated_at);
  const verdict    = decision?.verdict || narrative.verdict;
  const confidence = decision?.confidence || narrative.confidence;
  const verdictColor    = VERDICT_COLOR[verdict] || "gray";
  const confidenceColor = CONFIDENCE_COLOR[confidence] || "gray";
  const isInterim = !!(decision?.is_interim || narrative.is_interim);
  // DA-BUG-0240 — narrative_error lives at top-level on the persisted
  // payload, not nested inside narrative.
  const narrativeError = payload.narrative_error || data.narrative_error || null;
  const [rawOpen, setRawOpen] = React.useState(false);

  return (
    <VStack align="stretch" spacing={4}>
      {/* ─── Header bar — meta + Re-run ─── */}
      <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="lg" p={3}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
          <HStack spacing={3} fontSize="xs" color="purple.900" flexWrap="wrap">
            <Tag size="sm" colorScheme="purple"><TagLabel>{data.agent_version || "agent"}</TagLabel></Tag>
            {data.llm_model && <Text>model: <strong>{data.llm_model}</strong></Text>}
            <Text>generated <strong>{generatedAgo}</strong></Text>
            {data.baseline_days && <Text>baseline: <strong>{data.baseline_days}d</strong></Text>}
            {isInterim && <Tag size="sm" colorScheme="orange"><TagLabel>interim · campaign running</TagLabel></Tag>}
            {narrativeError && <Tag size="sm" colorScheme="red"><TagLabel>narrative invalid — deterministic-only</TagLabel></Tag>}
            {sampleSizeGrade && (
              <Tag size="sm" colorScheme={SAMPLE_GRADE_COLOR[sampleSizeGrade.grade] || "gray"}>
                <TagLabel>sample {sampleSizeGrade.grade} · {sampleSizeGrade.label}</TagLabel>
              </Tag>
            )}
          </HStack>
          <Button size="sm" leftIcon={<LuRefreshCw />} variant="outline" colorScheme="purple" onClick={onRerun}>Re-run</Button>
        </Flex>
        {data.stale && (
          <Alert status="warning" borderRadius="md" mt={2} py={1}>
            <AlertIcon boxSize={3} />
            <Text fontSize="xs">
              Campaign data has changed since this analysis (sent: {data.snapshot?.sent} → {data.live?.sent}). Re-run for fresh insights.
            </Text>
          </Alert>
        )}
      </Box>

      {/* ─── 1. Decision Summary (top of report — operator reads this first) ─── */}
      <Box border="2px solid" borderColor={`${verdictColor}.300`} borderRadius="lg" p={4} bg="white">
        <HStack mb={3} spacing={3} flexWrap="wrap">
          <Badge colorScheme={verdictColor} fontSize="sm" px={3} py={1} borderRadius="full">
            {(verdict || "—").replace(/_/g, " ")}
          </Badge>
          <Tag size="md" colorScheme={confidenceColor}>
            <TagLabel>confidence: {confidence || "—"}</TagLabel>
          </Tag>
        </HStack>
        <Text fontSize="lg" fontWeight="600" color="gray.800" lineHeight="1.4" mb={3}>
          {narrative.executive_headline || `${(verdict || "").replace(/_/g, " ")} — ${decision?.primary_diagnosis || "analysis pending"}`}
        </Text>
        {decision?.primary_diagnosis && (
          <Box mb={3}>
            <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px">
              Primary diagnosis
            </Text>
            <Text fontSize="sm" color="gray.800" mt={1}>{decision.primary_diagnosis}</Text>
          </Box>
        )}
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
          {decision?.recommended_next_move && (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" p={3}>
              <HStack mb={1}>
                <LuTarget color="#16a34a" size={14} />
                <Text fontSize="11px" fontWeight="600" color="green.800" textTransform="uppercase" letterSpacing="0.3px">
                  Recommended next move
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.800">{decision.recommended_next_move}</Text>
            </Box>
          )}
          {decision?.do_not && (
            <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={3}>
              <HStack mb={1}>
                <LuAlertTriangle color="#dc2626" size={14} />
                <Text fontSize="11px" fontWeight="600" color="red.800" textTransform="uppercase" letterSpacing="0.3px">
                  Do NOT
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.800">{decision.do_not}</Text>
            </Box>
          )}
        </Grid>
      </Box>

      {/* ─── 2. Why This Matters (deterministic — always renders) ─── */}
      {whyThisMatters && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
          <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={2}>
            Why this matters
          </Text>
          <Text fontSize="sm" color="gray.800" lineHeight="1.55" wordBreak="break-word">
            {whyThisMatters}
          </Text>
          {(primaryDriver || losingChannel) && (
            <HStack mt={3} spacing={2} flexWrap="wrap">
              {primaryDriver && (
                <Tag size="sm" colorScheme="green" variant="subtle">
                  <TagLabel>Driver: {primaryDriver.name}{primaryDriver.engagement_per_send != null ? ` · ${primaryDriver.engagement_per_send} eng/send` : ""}</TagLabel>
                </Tag>
              )}
              {losingChannel && (
                <Tag size="sm" colorScheme="red" variant="subtle">
                  <TagLabel>Weak point: {losingChannel.name} · 0 eng on {losingChannel.sent} sends</TagLabel>
                </Tag>
              )}
            </HStack>
          )}
        </Box>
      )}

      {/* ─── 3. Evidence — top 3 facts + top 3 risks + channel comparison ─── */}
      {(topEvidence.length > 0 || topRisks.length > 0 || (summary && summary.sent != null)) && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
          <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={3}>
            Evidence
          </Text>

          {/* Compact metrics row (always — these are the supporting numbers) */}
          {summary && (
            <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }} gap={2} mb={3}>
              <MetricTile label="Sent"      value={summary.sent}      colorScheme="blue" />
              <MetricTile label="Delivered" value={summary.delivered} subValue={summary.delivery_rate_pct != null ? `${summary.delivery_rate_pct}%` : null} colorScheme="green" />
              <MetricTile label="Failed"    value={summary.failed}    subValue={summary.failure_rate_pct != null ? `${summary.failure_rate_pct}%` : null} colorScheme="red" />
              <MetricTile label="Opened"    value={summary.opened}    subValue={summary.open_rate_pct != null ? `${summary.open_rate_pct}%` : null} colorScheme="purple" />
              <MetricTile label="Clicked"   value={summary.clicked}   subValue={summary.click_rate_pct != null ? `${summary.click_rate_pct}%` : null} colorScheme="orange" />
            </Grid>
          )}

          {/* Top evidence + top risks side-by-side */}
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={3}>
            {topEvidence.length > 0 && (
              <Box>
                <Text fontSize="11px" fontWeight="600" color="green.700" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                  Top {topEvidence.length} evidence
                </Text>
                <VStack align="stretch" spacing={1}>
                  {topEvidence.map((ev, i) => (
                    <Box key={i} borderLeft="3px solid" borderLeftColor="green.300" pl={2}>
                      <Text fontSize="xs" fontWeight="600" color="gray.800">
                        {ev.label}: <Text as="span" color="green.700">{ev.value}</Text>
                      </Text>
                      {ev.note && <Text fontSize="11px" color="gray.600" wordBreak="break-word">{ev.note}</Text>}
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
            {topRisks.length > 0 && (
              <Box>
                <Text fontSize="11px" fontWeight="600" color="orange.700" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                  Top {topRisks.length} risk{topRisks.length === 1 ? "" : "s"}
                </Text>
                <VStack align="stretch" spacing={1}>
                  {topRisks.map((rk, i) => (
                    <Box key={i} borderLeft="3px solid" borderLeftColor="orange.300" pl={2}>
                      <Text fontSize="xs" fontWeight="600" color="gray.800">{rk.label}</Text>
                      {rk.note && <Text fontSize="11px" color="gray.600" wordBreak="break-word">{rk.note}</Text>}
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </Grid>

          {/* Channel comparison (only multi-channel) */}
          {Array.isArray(summary?.channel_mix) && summary.channel_mix.length > 1 && (
            <Box mt={4}>
              <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                Channel comparison
              </Text>
              <Box overflowX="auto">
                <Box as="table" w="100%" fontSize="xs" sx={{ "& th, & td": { textAlign: "left", py: 1, px: 2 }, "& th": { color: "gray.500", fontWeight: 600 } }}>
                  <thead><tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <th>Channel</th><th>Sent</th><th>Delivered</th><th>Opened</th><th>Eng/Send</th><th>Share of engagement</th>
                  </tr></thead>
                  <tbody>
                    {summary.channel_mix.map((c) => (
                      <tr key={c.channel_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td><Tag size="sm" colorScheme={(c.engagement_per_send ?? 0) >= 0.01 ? "green" : "red"} variant="subtle"><TagLabel>{c.name}</TagLabel></Tag></td>
                        <td>{c.sent}</td>
                        <td>{c.delivered}{c.delivery_rate_pct != null && <Text as="span" color="gray.500"> ({c.delivery_rate_pct}%)</Text>}</td>
                        <td>{c.opened}</td>
                        <td><strong>{c.engagement_per_send != null ? c.engagement_per_send.toFixed(3) : "—"}</strong></td>
                        <td>{c.share_of_engaged_pct != null ? c.share_of_engaged_pct + "%" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            </Box>
          )}

          {/* Failure reasons (only when failures occurred) */}
          {summary?.deliverability?.available && summary.deliverability.total_failures > 0 && (
            <Box mt={4}>
              <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                Failure reasons ({summary.deliverability.total_failures} total)
              </Text>
              {summary.deliverability.dominant_failure && (
                <Text fontSize="xs" color="gray.700" wordBreak="break-word">
                  Dominant: <strong>"{summary.deliverability.dominant_failure.status_name}"</strong>{" "}
                  on {summary.deliverability.dominant_failure.channel}{" "}
                  ({summary.deliverability.dominant_failure.count} of {summary.deliverability.total_failures}).
                  <Text as="span" color="gray.500"> Remediation: {summary.deliverability.dominant_failure.remediation}</Text>
                </Text>
              )}
            </Box>
          )}

          {/* Campaign-level cost row (when available) */}
          {summary?.campaign_cost?.available && (
            <Box mt={4}>
              <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                Spend
              </Text>
              <Text fontSize="xs" color="gray.700" wordBreak="break-word">
                <strong>{summary.campaign_cost.currency} {summary.campaign_cost.actual_total_cost}</strong> spent
                {summary.campaign_cost.cost_per_opened != null && <> · {summary.campaign_cost.currency} {summary.campaign_cost.cost_per_opened} per opened</>}
                {summary.campaign_cost.cost_per_clicked != null && <> · {summary.campaign_cost.currency} {summary.campaign_cost.cost_per_clicked} per clicked</>}
                {summary.campaign_cost.planned_cost != null && <> · planned {summary.campaign_cost.currency} {summary.campaign_cost.planned_cost}</>}
              </Text>
            </Box>
          )}

          {/* LLM narrative blocks — only show ones the model actually populated */}
          {(narrative.what_worked || narrative.what_didnt || narrative.funnel_insight) && (
            <Box mt={4}>
              <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                Narrative
              </Text>
              <VStack align="stretch" spacing={2}>
                <NarrativeBlock title="What worked"        body={narrative.what_worked}        tone="green" />
                <NarrativeBlock title="What didn't"        body={narrative.what_didnt}         tone="red" />
                <NarrativeBlock title="Funnel insight"     body={narrative.funnel_insight}     tone="purple" />
                <NarrativeBlock title="Channel attribution" body={narrative.channel_attribution} tone="blue" />
                <NarrativeBlock title="vs. 90-day baseline" body={narrative.vs_baseline_summary} tone="teal" />
                <NarrativeBlock title="vs. comparable past" body={narrative.vs_comparable_summary} tone="teal" />
                <NarrativeBlock title="Audience signal"    body={narrative.audience_signal}    tone="gray" />
              </VStack>
            </Box>
          )}
        </Box>
      )}

      {/* ─── 4. Recommended Experiments ─── */}
      {Array.isArray(experiments) && experiments.length > 0 && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} bg="white">
          <HStack mb={3}>
            <LuSparkles color="#8B5CF6" size={18} />
            <Text fontWeight="600" fontSize="sm">Recommended experiments ({experiments.length})</Text>
          </HStack>
          <VStack align="stretch" spacing={3}>
            {experiments.map((exp, i) => (
              <Box key={i} borderLeft="3px solid" borderLeftColor="purple.300" pl={3} py={1}>
                <Flex justify="space-between" align="flex-start" gap={3} flexWrap="wrap" mb={1}>
                  <Text fontWeight="600" color="gray.800" wordBreak="break-word">{exp.title}</Text>
                  {exp.owner_tool && exp.owner_tool !== "manual" && (
                    <Tag size="sm" colorScheme="purple" variant="subtle"><TagLabel>↗ {exp.owner_tool}</TagLabel></Tag>
                  )}
                </Flex>
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2} fontSize="xs">
                  {exp.hypothesis && (
                    <Box>
                      <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="10px">Hypothesis</Text>
                      <Text color="gray.700" wordBreak="break-word">{exp.hypothesis}</Text>
                    </Box>
                  )}
                  {exp.setup && (
                    <Box>
                      <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="10px">Audience / Setup</Text>
                      <Text color="gray.700" wordBreak="break-word">{exp.setup}</Text>
                    </Box>
                  )}
                  {exp.success_metric && (
                    <Box>
                      <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="10px">Success metric</Text>
                      <Text color="gray.700" wordBreak="break-word">{exp.success_metric}</Text>
                    </Box>
                  )}
                  {exp.minimum_sample && (
                    <Box>
                      <Text fontWeight="600" color="gray.500" textTransform="uppercase" fontSize="10px">Duration / sample</Text>
                      <Text color="gray.700" wordBreak="break-word">{exp.minimum_sample}</Text>
                    </Box>
                  )}
                </Grid>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {/* LLM-prose "recommended_next" — short complementary list, only when present + valid */}
      {Array.isArray(narrative.recommended_next) && narrative.recommended_next.length > 0 && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={3} bg="white">
          <HStack mb={2}>
            <LuTrendingUp color="#16a34a" size={16} />
            <Text fontWeight="600" fontSize="sm">Quick recommended actions</Text>
          </HStack>
          <VStack align="stretch" spacing={2}>
            {narrative.recommended_next.map((rec, i) => (
              <Flex key={i} justify="space-between" align="flex-start" gap={3} flexWrap="wrap"
                     borderLeft="3px solid" borderLeftColor="gray.200" pl={3} py={1}>
                <Box flex="1" minW={0}>
                  <Text fontWeight="500" fontSize="sm" color="gray.800" wordBreak="break-word">{rec.title}</Text>
                  {rec.why && <Text fontSize="xs" color="gray.600" mt={1} wordBreak="break-word">{rec.why}</Text>}
                  {rec.expected_impact && (
                    <Text fontSize="11px" color="green.700" fontWeight="500" mt={1}>
                      Expected impact: {rec.expected_impact}
                    </Text>
                  )}
                </Box>
                {rec.tool && rec.tool !== "manual" && (
                  <Tag size="sm" colorScheme="purple" variant="subtle"><TagLabel>↗ {rec.tool}</TagLabel></Tag>
                )}
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      {/* ─── 5. Data Gaps (clear about what's limiting confidence) ─── */}
      {dataGaps.length > 0 && (
        <Box border="1px solid" borderColor="gray.300" borderRadius="lg" p={3} bg="gray.50">
          <HStack mb={2}>
            <LuAlertTriangle color="#6b7280" size={16} />
            <Text fontWeight="600" fontSize="sm" color="gray.700">Data gaps limiting confidence ({dataGaps.length})</Text>
          </HStack>
          <VStack align="stretch" spacing={1}>
            {dataGaps.map((gap, i) => (
              <Flex key={i} gap={2} align="flex-start" flexWrap="wrap">
                <Tag size="sm" variant="outline" colorScheme="gray" flexShrink={0}>
                  <TagLabel>{gap.label}</TagLabel>
                </Tag>
                <Text fontSize="xs" color="gray.600" wordBreak="break-word" flex="1" minW="240px">{gap.reason}</Text>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      {/* ─── 6. Raw Metrics — collapsible, lower priority ─── */}
      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" bg="white">
        <Flex p={3} cursor="pointer" onClick={() => setRawOpen((v) => !v)}
              justify="space-between" align="center" _hover={{ bg: "gray.50" }} borderRadius="lg">
          <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px">
            Raw metrics & dimensional details
          </Text>
          <Text fontSize="xs" color="gray.500">{rawOpen ? "− hide" : "+ show"}</Text>
        </Flex>
        {rawOpen && (
          <Box px={3} pb={3}>
            <Divider mb={3} />
            <Grid templateColumns="repeat(4, 1fr)" gap={2} mb={3}>
              <MetricTile label="Sent"      value={summary.sent}      colorScheme="blue" />
              <MetricTile label="Delivered" value={summary.delivered} subValue={summary.delivery_rate_pct != null ? `${summary.delivery_rate_pct}%` : null} colorScheme="green" />
              <MetricTile label="Opened"    value={summary.opened}    subValue={summary.open_rate_pct != null ? `${summary.open_rate_pct}%` : null} colorScheme="purple" />
              <MetricTile label="Clicked"   value={summary.clicked}   subValue={summary.click_rate_pct != null ? `${summary.click_rate_pct}%` : null} colorScheme="orange" />
            </Grid>

            {/* Send-time */}
            {summary?.send_time && (
              <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                <LuClock color="var(--brand-primary)" size={14} />
                <Text fontSize="xs" color="gray.700" wordBreak="break-word">{summary.send_time.sentence}</Text>
              </Flex>
            )}

            {/* Frequency */}
            {summary?.frequency && (
              <Flex align="center" gap={2} mb={2} flexWrap="wrap">
                <LuRepeat color="#0891b2" size={14} />
                <Text fontSize="xs" color="gray.700" wordBreak="break-word">{summary.frequency.sentence}</Text>
              </Flex>
            )}

            {/* Statistical confidence */}
            {summary?.significance && Array.isArray(summary.significance.pairwise) && summary.significance.pairwise.length > 0 && (
              <Box mb={2}>
                <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                  Statistical confidence
                </Text>
                <VStack align="stretch" spacing={1}>
                  {summary.significance.pairwise.map((p, i) => (
                    <Flex key={i} gap={2} align="flex-start" flexWrap="wrap">
                      <Tag size="sm" colorScheme={p.statistically_significant ? "green" : "yellow"} flexShrink={0}>
                        <TagLabel>{p.statistically_significant ? "significant" : "noise"}</TagLabel>
                      </Tag>
                      <Text fontSize="xs" color="gray.700" wordBreak="break-word" flex="1" minW="200px">{p.note}</Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Creative findings */}
            {summary?.creative?.available && Array.isArray(summary.creative.findings) && summary.creative.findings.length > 0 && (
              <Box mb={2}>
                <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.3px" mb={1}>
                  Creative findings
                </Text>
                <VStack align="stretch" spacing={1}>
                  {summary.creative.findings.map((f, i) => (
                    <Text key={i} fontSize="xs" color={f.severity === "warn" ? "orange.700" : "gray.600"} wordBreak="break-word">
                      {f.severity === "warn" ? "⚠" : "ⓘ"} {f.message}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Intent alignment */}
            {summary?.intent && (
              <Text fontSize="xs" color="gray.700" wordBreak="break-word">
                Campaign intent: <strong>{summary.intent.campaign_type}</strong> · primary KPI <strong>{summary.intent.primary_kpi}</strong> — {summary.intent.expectation}
              </Text>
            )}
          </Box>
        )}
      </Box>
    </VStack>
  );
}

// ─── Small primitives ─────────────────────────────────────────────

function MetricTile({ label, value, subValue, colorScheme = "blue" }) {
  return (
    <Box p={3} borderRadius="lg" bg="white" border="1px solid" borderColor="gray.200">
      <Text fontSize="xs" color="gray.500">{label}</Text>
      <Text fontSize="2xl" fontWeight="600" color={`${colorScheme}.600`} lineHeight="1.1" mt={1}>
        {value ?? 0}
      </Text>
      {subValue && <Text fontSize="xs" color="gray.500" mt={1}>{subValue}</Text>}
    </Box>
  );
}

function ContextCard({ icon, title, body, metric }) {
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={3} bg="white">
      <HStack mb={2} color="gray.600">
        {icon}
        <Text fontWeight="600" fontSize="sm" color="gray.700">{title}</Text>
      </HStack>
      {body
        ? <Text fontSize="sm" color="gray.700">{body}</Text>
        : <Text fontSize="xs" color="gray.400" fontStyle="italic">Not enough data.</Text>}
      {metric?.delta_pts != null && (
        <HStack mt={2} spacing={1}>
          <Icon as={metric.delta_pts > 0 ? LuTrendingUp : metric.delta_pts < 0 ? LuTrendingDown : LuMinus}
                color={metric.delta_pts > 0 ? "green.500" : metric.delta_pts < 0 ? "red.500" : "gray.500"} boxSize={3} />
          <Text fontSize="xs" color={metric.delta_pts > 0 ? "green.700" : metric.delta_pts < 0 ? "red.700" : "gray.700"}>
            {metric.delta_pts > 0 ? "+" : ""}{metric.delta_pts} pts vs baseline ({metric.baseline}%)
          </Text>
        </HStack>
      )}
      {metric?.label && metric?.value && (
        <HStack mt={2} spacing={1}>
          <Icon as={LuClock} color="gray.500" boxSize={3} />
          <Text fontSize="xs" color="gray.700"><strong>{metric.label}</strong>: {metric.value}</Text>
        </HStack>
      )}
    </Box>
  );
}

function NarrativeBlock({ title, body, tone = "gray", footer }) {
  if (!body) return null;
  return (
    <Box border="1px solid" borderColor={`${tone}.200`} borderLeft="3px solid"
         borderLeftColor={`${tone}.400`} borderRadius="md" px={3} py={2} bg="white">
      <Text fontSize="11px" fontWeight="600" color={`${tone}.700`} textTransform="uppercase" letterSpacing="0.3px">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.800" mt={1}>{body}</Text>
      {footer}
    </Box>
  );
}

function humanAgo(ts) {
  if (!ts) return "just now";
  const dt = new Date(ts);
  const sec = Math.max(1, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (sec < 60)  return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

const VERDICT_COLOR = {
  outperforming: "green",
  on_baseline:   "blue",
  underperforming: "red",
  mixed:         "yellow",
  inconclusive_small_sample: "gray",
};
const CONFIDENCE_COLOR = { high: "green", medium: "yellow", low: "gray" };
const SAMPLE_GRADE_COLOR = { A: "green", B: "teal", C: "yellow", D: "gray" };
